import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { customersApi, portaoneApi } from '../services/api';
import './PortaOne.css';

/**
 * PortaOne (PortaBilling reseller) - voice usage billing.
 * The 20th-to-20th "Cost/Revenue statistics" report is reproduced from xDRs:
 * revenue = Voice Calls charged to each customer (VAT excl), cost = the
 * reseller's wholesale voice xDRs per customer. Mapped customers get a
 * "Voice calls usage" line in Invoice Prep (qty 1, sell = revenue, cost =
 * wholesale). The emailed CSV can be imported to reconcile against the API.
 */

interface Row {
  i_customer: number; name: string; companyname: string | null; email: string | null; balance: number | null;
  bill_status: string | null; blocked: boolean; voice_revenue: string | null; voice_cost: string | null;
  calls: number; minutes: number; report_revenue: string | null; report_cost: string | null;
  customer_id: number | null; customer_name: string | null; last_synced_at: string | null;
}
interface SimpleCustomer { id: number; xero_name: string; }

const ZAR = (v: string | number | null | undefined) =>
  v == null || v === '' ? '—' : `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const HIDDEN_KEY = 'cv-portaone-hidden-customers';
const loadHidden = (): Set<number> => { try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || '[]')); } catch { return new Set(); } };
const saveHidden = (s: Set<number>) => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(s))); } catch { /* ignore */ } };

const fmtDay = (s: string) => new Date(`${s}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

/** Type-ahead customer picker: filters the CloudView customer list as you type. */
function CustomerPicker({ value, valueName, customers, disabled, onSelect, onClear }: {
  value: number | null; valueName: string | null; customers: SimpleCustomer[]; disabled: boolean;
  onSelect: (id: number) => void; onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? customers.filter(c => c.xero_name.toLowerCase().includes(needle)) : customers;
    return list.slice(0, 40);
  }, [q, customers]);
  useEffect(() => { setIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const pick = (c: SimpleCustomer) => { onSelect(c.id); setOpen(false); setQ(''); };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (matches[idx]) pick(matches[idx]); }
    else if (e.key === 'Escape') { setOpen(false); setQ(''); }
  };
  return (
    <div className={`po-picker ${value ? 'has-value' : ''}`} ref={boxRef}>
      <input
        className="po-picker-input"
        placeholder={value ? valueName || `#${value}` : 'Type to match a customer…'}
        value={q}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
        title={value ? `Mapped to ${valueName}` : 'Not billed - type to search'}
      />
      {value && !q && <button className="po-picker-clear" disabled={disabled} onClick={onClear} title="Unmap (stop billing this PortaOne customer)">✕</button>}
      {open && !disabled && (
        <ul className="po-picker-list" role="listbox">
          {matches.length === 0 && <li className="po-picker-empty">No customer matches "{q}"</li>}
          {matches.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === idx} className={`${i === idx ? 'active' : ''} ${c.id === value ? 'current' : ''}`}
                onMouseEnter={() => setIdx(i)} onMouseDown={e => { e.preventDefault(); pick(c); }}>
              {c.xero_name}{c.id === value ? ' ✓' : ''}
            </li>
          ))}
          {customers.length > matches.length && matches.length === 40 && <li className="po-picker-empty">Keep typing to narrow down…</li>}
        </ul>
      )}
    </div>
  );
}

function PortaOne() {
  const [status, setStatus] = useState<any>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [period, setPeriod] = useState<{ start: string; end: string } | null>(null);
  const [customers, setCustomers] = useState<SimpleCustomer[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'mapped' | 'unmapped' | 'billable' | 'hidden'>('all');
  const [hidden, setHidden] = useState<Set<number>>(loadHidden);
  const toggleHidden = (id: number) => setHidden(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); saveHidden(n); return n; });
  const unhideAll = () => { const n = new Set<number>(); saveHidden(n); setHidden(n); if (filter === 'hidden') setFilter('all'); };
  const [search, setSearch] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [s, c, cust] = await Promise.all([portaoneApi.status(), portaoneApi.customers(), customersApi.list()]);
      setStatus(s); setRows(c.customers); setPeriod({ start: c.period_start, end: c.period_end });
      setCustomers((cust as any[]).map(x => ({ id: x.id, xero_name: x.xero_name })).sort((a, b) => a.xero_name.localeCompare(b.xero_name)));
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load PortaOne');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>, ok?: (r: any) => string) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok(r)); await load(); return r; }
    catch (e: any) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(null); }
  };

  const sync = () => act('sync', () => portaoneApi.sync(),
    r => `${r.customers} customers · ${fmtDay(r.period_start)} → ${fmtDay(r.period_end)} · revenue ${ZAR(r.voice_revenue)} / cost ${ZAR(r.voice_cost)}${r.errors?.length ? ` · ${r.errors.length} errors` : ''}`);
  const autoMap = () => act('automap', () => portaoneApi.autoMap(), r => `${r.matched} matched · ${r.unmatched} still unmapped`);
  const importReport = async (f: File) => {
    await act('import', () => portaoneApi.importReport(f), r =>
      `${r.matched} of ${r.lines} report lines matched (${fmtDay(r.period_start)} → ${fmtDay(r.period_end)})${r.revenue_deltas.length ? ` · ${r.revenue_deltas.length} revenue differences` : ' · all revenue figures agree'}`);
    if (fileRef.current) fileRef.current.value = '';
  };

  const visible = useMemo(() => rows
    .filter(r => filter === 'hidden' ? hidden.has(r.i_customer) : !hidden.has(r.i_customer))
    .filter(r => filter === 'all' || filter === 'hidden' || (filter === 'mapped' ? !!r.customer_id : filter === 'unmapped' ? !r.customer_id : Number(r.voice_revenue || 0) > 0))
    .filter(r => !search || `${r.name} ${r.companyname || ''} ${r.customer_name || ''}`.toLowerCase().includes(search.toLowerCase())),
    [rows, filter, search, hidden]);
  const totals = useMemo(() => visible.reduce((a, r) => ({
    rev: a.rev + Number(r.voice_revenue || 0), cost: a.cost + Number(r.voice_cost || 0), rep: a.rep + Number(r.report_revenue || 0),
  }), { rev: 0, cost: 0, rep: 0 }), [visible]);
  const delta = (r: Row) => (r.report_revenue != null && r.voice_revenue != null) ? Number(r.report_revenue) - Number(r.voice_revenue) : null;

  return (
    <div className="portaone-page">
      <div className="page-header">
        <h1>PortaOne</h1>
        <p className="page-description">Voice usage from PortaBilling, 20th to 20th, VAT excluded. Map each PortaOne customer once; usage flows into Invoice Prep every sync.</p>
      </div>

      {status && (
        <div className="po-strip">
          <span className={`po-pill ${status.configured ? 'ok' : 'warn'}`}>{status.configured ? '● Configured' : '○ Not configured (PORTAONE_* env)'}</span>
          {period && <span className="po-pill">Window {fmtDay(period.start)} → {fmtDay(period.end)}</span>}
          <span className="po-pill">{status.customers} customers · {status.mapped} mapped</span>
          <span className="po-pill">Revenue {ZAR(status.voice_revenue)} · Cost {ZAR(status.voice_cost)}</span>
          {status.last_sync && <span className="po-pill" title={status.last_sync.message || ''}>Last sync {status.last_sync.status} {fmtDate(status.last_sync.finished_at)}</span>}
          <span className="po-grow" />
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                 onChange={e => e.target.files?.[0] && importReport(e.target.files[0])} />
          <button className="btn btn-sm btn-outline" disabled={busy != null} onClick={() => fileRef.current?.click()} title="Upload the emailed 'Cost/Revenue statistics' CSV to reconcile against the API figures">
            📎 Import 20th report
          </button>
          <button className="btn btn-sm btn-outline" disabled={busy != null} onClick={autoMap}>✨ Auto-map by name</button>
          <button className="btn btn-sm btn-primary" disabled={busy === 'sync' || !status.configured} onClick={sync}>{busy === 'sync' ? '⏳ Syncing…' : '🔄 Sync PortaOne now'}</button>
        </div>
      )}

      <div className="po-toolbar">
        {(['all', 'billable', 'mapped', 'unmapped'] as const).map(f => (
          <button key={f} className={`po-chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
        {hidden.size > 0 && (
          <>
            <button className={`po-chip ${filter === 'hidden' ? 'active' : ''}`} onClick={() => setFilter(filter === 'hidden' ? 'all' : 'hidden')} title="Show only the customers you have hidden">
              🙈 Hidden ({hidden.size})
            </button>
            {filter === 'hidden' && <button className="po-chip" onClick={unhideAll}>Unhide all</button>}
          </>
        )}
        <input className="po-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <table className="po-table">
        <thead>
          <tr>
            <th>PortaOne customer</th><th>Status</th><th className="num">Calls</th><th className="num">Minutes</th>
            <th className="num">Revenue (bill)</th><th className="num">Cost</th><th className="num">Margin</th>
            <th className="num" title="Revenue in the emailed 20th report vs the API">Report Δ</th><th>CloudView customer</th><th></th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && <tr><td colSpan={10} className="po-empty">{rows.length === 0 ? 'No PortaOne customers cached yet. Click Sync PortaOne now.' : 'Nothing matches.'}</td></tr>}
          {visible.map(r => {
            const d = delta(r);
            const margin = r.voice_revenue != null && r.voice_cost != null ? Number(r.voice_revenue) - Number(r.voice_cost) : null;
            return (
              <tr key={r.i_customer} className={`${r.customer_id ? 'mapped' : 'unmapped'}${!r.calls && !Number(r.voice_revenue || 0) && !Number(r.voice_cost || 0) ? ' no-usage' : ''}`}
                  title={!r.calls && !Number(r.voice_revenue || 0) && !Number(r.voice_cost || 0) ? 'No calls in this billing window - nothing is billed, no line in Invoice Prep' : undefined}>
                <td>
                  <div className="po-name">{r.name}{!r.calls && !Number(r.voice_revenue || 0) && !Number(r.voice_cost || 0) && <span className="po-tag danger" title="No usage this window">no usage · not billed</span>}</div>
                  <div className="po-muted">#{r.i_customer}{r.companyname ? ` · ${r.companyname}` : ''}</div>
                </td>
                <td>
                  <span className={`po-status ${r.blocked ? 'blocked' : r.bill_status === 'O' ? 'open' : 'other'}`}>{r.blocked ? 'blocked' : r.bill_status === 'O' ? 'open' : r.bill_status || '—'}</span>
                </td>
                <td className="num">{r.calls}</td>
                <td className="num">{r.minutes}</td>
                <td className="num"><strong>{ZAR(r.voice_revenue)}</strong></td>
                <td className="num">{ZAR(r.voice_cost)}</td>
                <td className={`num ${margin != null && margin < 0 ? 'neg' : ''}`}>{margin == null ? '—' : ZAR(margin)}</td>
                <td className={`num ${d != null && Math.abs(d) >= 0.01 ? 'neg' : 'po-muted'}`} title={r.report_revenue != null ? `Report: ${ZAR(r.report_revenue)}` : 'No report imported for this window'}>
                  {d == null ? '—' : Math.abs(d) < 0.01 ? '✓' : ZAR(d)}
                </td>
                <td className="po-map-cell">
                  <CustomerPicker value={r.customer_id} valueName={r.customer_name} customers={customers} disabled={busy != null}
                                  onSelect={id => act(`m${r.i_customer}`, () => portaoneApi.map(r.i_customer, id), () => `Mapped ${r.name}`)}
                                  onClear={() => act(`u${r.i_customer}`, () => portaoneApi.unmap(r.i_customer), () => `Unmapped ${r.name}`)} />
                </td>
                <td className="po-hide">
                  <button className="po-hide-btn" onClick={() => toggleHidden(r.i_customer)}
                          title={hidden.has(r.i_customer) ? 'Unhide this customer' : 'Hide this customer from the list and totals (remembered in this browser)'}>
                    {hidden.has(r.i_customer) ? '👁' : '🙈'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        {visible.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4}>{visible.length} customers</td>
              <td className="num"><strong>{ZAR(totals.rev)}</strong></td>
              <td className="num">{ZAR(totals.cost)}</td>
              <td className="num">{ZAR(totals.rev - totals.cost)}</td>
              <td className="num po-muted">{totals.rep ? ZAR(totals.rep - totals.rev) : '—'}</td>
              <td colSpan={2} className="po-muted">{hidden.size > 0 && filter !== 'hidden' ? `${hidden.size} hidden` : ''}</td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="po-hint">
        Revenue is what PortaBilling charged the customer for voice calls in the window (VAT excluded, taxes are billed separately).
        Cost is what Session Telecoms charged Cloud ITS for the same calls. Mapped customers get a <strong>Voice calls usage</strong> line in Invoice Prep
        with sell = revenue and cost = wholesale, refreshed on every sync and frozen by "Bill now".
      </p>
    </div>
  );
}

export default PortaOne;
